import { z } from 'zod';
import { validateDomain } from '../../../../schemas/domain.schema';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { toHttpsUrl } from '../../../../utils/urlUtils';
import { connectsThroughRollout, ROLLOUT_CRMS } from '../../../../constants/rolloutCrms';

const INVALID_WEB_URL_MESSAGE = 'Please enter a valid URL (e.g., yourcompany.com)';

/** True when the value (with or without https:// / www.) is a web address on a real domain. */
export const isValidWebUrl = (value: string): boolean => {
  const url = toHttpsUrl(value);
  if (!url) return false;
  try {
    return validateDomain(new URL(url).hostname).isValid;
  } catch {
    return false;
  }
};

// Web address fields use HttpsUrlInput; these store the value as https://domain.com/path.
const requiredWebUrl = (requiredMessage: string) =>
  z
    .string()
    .trim()
    .min(1, requiredMessage)
    .refine(isValidWebUrl, { message: INVALID_WEB_URL_MESSAGE })
    .transform(toHttpsUrl);

const optionalWebUrl = z
  .string()
  .optional()
  .refine((val) => !val?.trim() || isValidWebUrl(val), { message: INVALID_WEB_URL_MESSAGE })
  .transform((val) => (val?.trim() ? toHttpsUrl(val) : val));

// Stage 1: Organization Setup
export const organizationSetupSchema = z.object({
  org_name: z.string().min(1, 'Company/Agent name is required'),
  client_address: z.object({
    street: z.string().min(1, 'Street address is required'),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    zip: z.string().min(1, 'ZIP code is required'),
    country: z.enum(['US', 'Canada']),
  }),
  org_phone: z.string().min(1, 'Phone number is required'),
  email: z.string().email('Valid email is required'),
  notification_email: z.string().email('Valid notification email is required'),
  timezone: z.string().min(1, 'Timezone is required'),
});

export type OrganizationSetupData = z.infer<typeof organizationSetupSchema>;

// Stage 3: Remi Setup Schema (formerly Stage 2)
export const remiSetupSchema = z.object({
  // Voice Setup
  remi_intro_name: z.string().min(1, 'Introduction name is required'),
  assistant_name: z.string().min(1, 'Assistant name is required'),
  assistant_name_pronunciation: z.string().min(1, 'Pronunciation is required'),
  voice: z.string().min(1, 'Voice selection is required'),
  background_track: z.string().optional(),

  // Knowledge Base Setup
  kb_name: z.string().min(1, 'Knowledge base name is required'),
  service_type: z.string().min(1, 'Service type is required'),
  service_areas: z.string().min(1, 'Service areas are required'),
  why_work_with_you: z.string().min(1, 'This field is required'),
  website_url: requiredWebUrl('Website URL is required'),
  custom_questions: z
    .array(
      z.object({
        question: z.string(),
        answer: z.string(),
      })
    )
    .optional(),
});

export type RemiSetupData = z.infer<typeof remiSetupSchema>;

// Stage 4: Twilio/Phone Number Setup Schema (formerly Stage 3)
export const AREA_CODE_PATTERN = /^\d{3}$/;

export const isValidAreaCode = (value: string): boolean => AREA_CODE_PATTERN.test(value);

export const twilioSetupSchema = z
  .object({
    // Phone Number Preferences
    preferred_area_code: z.string().min(3, 'Area code is required'),
    preferred_location: z.string().min(1, 'Preferred location is required'),
    other_area_codes: z
      .array(z.string().regex(AREA_CODE_PATTERN, 'Area codes must be 3 digits'))
      .min(1, 'Add at least one other acceptable area code'),
    area_code_acknowledgement: z.boolean().optional(),

    // Business Verification
    legal_business_name: z.string().min(1, 'Legal business name is required'),
    business_address: z.object({
      street: z.string().min(1, 'Street address is required'),
      city: z.string().min(1, 'City is required'),
      state: z.string().min(1, 'State is required'),
      zip: z.string().min(1, 'ZIP code is required'),
      country: z.enum(['US', 'Canada']),
    }),
    business_type: z.enum(['LLC', 'Corporation', 'Sole Proprietorship']),
    ein_cbn: z.string().min(1, 'EIN/CBN is required'),
    business_website: requiredWebUrl('Business website is required'),
  })
  .superRefine((data, ctx) => {
    // If other_area_codes has values, area_code_acknowledgement must be true
    if (data.other_area_codes && data.other_area_codes.length > 0) {
      if (!data.area_code_acknowledgement) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please acknowledge understanding of alternative area codes',
          path: ['area_code_acknowledgement'],
        });
      }
    }
  });

export type TwilioSetupData = z.infer<typeof twilioSetupSchema>;

// Stage 5: CRM Setup Schema
// Rollout CRMs (connected via the Connect popup) take their labels from
// ROLLOUT_CRMS; the rest collect an API key or login details instead.
export const CRM_OPTIONS = [
  { value: 'fub', label: ROLLOUT_CRMS.fub.label },
  { value: 'cinc', label: ROLLOUT_CRMS.cinc.label },
  { value: 'brivity', label: ROLLOUT_CRMS.brivity.label },
  { value: 'boldtrail', label: ROLLOUT_CRMS.boldtrail.label },
  { value: 'sierra_interactive', label: ROLLOUT_CRMS.sierra_interactive.label },
  { value: 'sure_send', label: ROLLOUT_CRMS.sure_send.label },
  { value: 'salesforce', label: ROLLOUT_CRMS.salesforce.label },
  { value: 'lofty', label: ROLLOUT_CRMS.lofty.label },
  { value: 'command', label: 'Command' },
  { value: 'boomtown', label: 'Boomtown' },
  { value: 'gohighlevel', label: 'GoHighLevel' },
  { value: 'realgeeks', label: 'Real Geeks' },
  { value: 'compass', label: 'Compass' },
  { value: 'other', label: 'Other (enter below)' },
] as const;

/** CRMs not offered in onboarding for now; existing entries keep showing them. */
export const HIDDEN_CRM_OPTIONS: readonly string[] = ['sure_send', 'salesforce'];

export const OFFERED_CRM_OPTIONS = CRM_OPTIONS.filter(
  (option) => !HIDDEN_CRM_OPTIONS.includes(option.value)
);

// Who receives the CRM's Two-Factor Authentication codes: the onboarding user
// ("me", prefilled from their profile) or someone else they name.
export const TWO_FA_CONTACT_TYPES = ['me', 'other'] as const;
export type TwoFaContactType = (typeof TWO_FA_CONTACT_TYPES)[number];

const isValidEmail = (value: string): boolean => z.string().email().safeParse(value).success;

interface TwoFaContactValues {
  two_fa_contact_type?: TwoFaContactType;
  two_fa_contact_name?: string;
  two_fa_contact_email?: string;
  two_fa_contact_phone?: string;
}

/** Requires a complete 2FA contact (who, plus a valid name, email and phone). */
const addTwoFaContactIssues = (
  data: TwoFaContactValues,
  ctx: z.RefinementCtx,
  path: (string | number)[] = []
) => {
  const issue = (field: keyof TwoFaContactValues, message: string) =>
    ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [...path, field] });
  if (!data.two_fa_contact_type) issue('two_fa_contact_type', 'Choose who receives the 2FA codes');
  if (!data.two_fa_contact_name?.trim()) {
    issue('two_fa_contact_name', '2FA contact name is required');
  }
  if (!data.two_fa_contact_email?.trim()) {
    issue('two_fa_contact_email', '2FA contact email is required');
  } else if (!isValidEmail(data.two_fa_contact_email.trim())) {
    issue('two_fa_contact_email', 'Please enter a valid email address');
  }
  if (!data.two_fa_contact_phone?.trim()) {
    issue('two_fa_contact_phone', '2FA contact phone is required');
  } else if (!isValidPhoneNumber(data.two_fa_contact_phone)) {
    issue('two_fa_contact_phone', 'Please enter a valid phone number');
  }
};

// Single CRM entry schema
export const crmEntrySchema = z
  .object({
    // CRM Selection
    crm_type: z.string().min(1, 'Please select your CRM'),
    crm_other_name: z.string().optional(),
    ai_disclaimer_acknowledged: z.boolean().optional(),

    // API Key (if available)
    super_admin_api_key: z.string().optional(),

    // Fallback credentials (if API key not available)
    login_url: optionalWebUrl,
    super_admin_username: z.string().optional(),
    super_admin_password: z.string().optional(),
    two_fa_contact_type: z.enum(TWO_FA_CONTACT_TYPES).optional(),
    two_fa_contact_name: z.string().optional(),
    two_fa_contact_email: z.string().optional(),
    two_fa_contact_phone: z.string().optional(),

    // Brivity-specific fields
    brivity_website_login_url: optionalWebUrl,
    brivity_username: z.string().optional(),
    brivity_password: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.crm_type && !data.ai_disclaimer_acknowledged) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please confirm the AI CRM integration acknowledgment before continuing',
        path: ['ai_disclaimer_acknowledged'],
      });
    }

    // If CRM is "other", require the other name
    if (data.crm_type === 'other') {
      if (!data.crm_other_name || data.crm_other_name.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please enter your CRM name',
          path: ['crm_other_name'],
        });
      }
    }

    // Every CRM needs a 2FA contact. Rollout CRMs connect through the Connect
    // popup, so no credentials; any other CRM also needs account access (the
    // API key is optional).
    if (data.crm_type) addTwoFaContactIssues(data, ctx);
    if (data.crm_type && !connectsThroughRollout(data.crm_type)) {
      if (!data.login_url || data.login_url.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Login URL is required',
          path: ['login_url'],
        });
      }
      if (!data.super_admin_username || data.super_admin_username.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Account owner username is required',
          path: ['super_admin_username'],
        });
      }
      if (!data.super_admin_password || data.super_admin_password.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Account owner password is required',
          path: ['super_admin_password'],
        });
      }
    }
  });

export type CRMEntryData = z.infer<typeof crmEntrySchema>;

// Stage 4: IDX — clients use a third-party IDX and share where it is displayed,
// plus the domain Remi sends email from (SPEC-2741).

export const EMAIL_DOMAIN_SETUP_OPTIONS = ['self', 'speculo'] as const;
export const EMAIL_DOMAIN_SOURCES = ['same_as_idx', 'different'] as const;
export const DOMAIN_ACCESS_METHODS = ['godaddy_delegate', 'credentials'] as const;

export const DOMAIN_PROVIDERS = [
  { value: 'godaddy', label: 'GoDaddy' },
  { value: 'namecheap', label: 'Namecheap' },
  { value: 'cloudflare', label: 'Cloudflare' },
  { value: 'squarespace', label: 'Squarespace Domains' },
  { value: 'bluehost', label: 'Bluehost' },
  { value: 'wix', label: 'Wix' },
  { value: 'other', label: 'Other' },
] as const;
type DomainProvider = (typeof DOMAIN_PROVIDERS)[number]['value'];
const DOMAIN_PROVIDER_VALUES = DOMAIN_PROVIDERS.map((p) => p.value) as [
  DomainProvider,
  ...DomainProvider[],
];

/** Removes a typed or pasted scheme, `www.`, path, query and trailing dot/slash. */
export const cleanDomainInput = (value: string): string =>
  value
    .trim()
    .replace(/^(?:https?:\/\/|www\.)+/i, '')
    .replace(/[/?#].*$/, '')
    .replace(/\.+$/, '')
    .toLowerCase();

// Second-level labels that are part of a country's public suffix (e.g. co.uk).
const SECOND_LEVEL_SUFFIXES = new Set(['co', 'com', 'net', 'org', 'gov', 'edu', 'ac']);

/**
 * Root domain of a URL or host: "https://www.abcrealty.com/search" → "abcrealty.com",
 * "search.abcrealty.co.uk" → "abcrealty.co.uk". Returns '' when there's no domain.
 */
export const toRootDomain = (urlOrHost: string): string => {
  const labels = cleanDomainInput(urlOrHost).split('.').filter(Boolean);
  if (labels.length < 2) return '';
  const tld = labels[labels.length - 1];
  const sld = labels[labels.length - 2];
  const keep = tld.length === 2 && SECOND_LEVEL_SUFFIXES.has(sld) && labels.length > 2 ? 3 : 2;
  return labels.slice(-keep).join('.');
};

const emailDomainSchema = z.object({
  setup_option: z.enum(EMAIL_DOMAIN_SETUP_OPTIONS).optional(),
  domain_source: z.enum(EMAIL_DOMAIN_SOURCES).optional(),
  /** Typed domain when `domain_source` is "different". */
  domain: z.string().optional(),
  sendgrid_confirmed: z.boolean().optional(),
  /** Option A: the domain the shown DNS records were generated for. */
  dns_records_domain: z.string().optional(),
  access_method: z.enum(DOMAIN_ACCESS_METHODS).optional(),
  godaddy_access_confirmed: z.boolean().optional(),
  provider: z.enum(DOMAIN_PROVIDER_VALUES).optional(),
  provider_other_name: z.string().optional(),
  login_url: optionalWebUrl,
  username: z.string().optional(),
  password: z.string().optional(),
  two_fa_contact_type: z.enum(TWO_FA_CONTACT_TYPES).optional(),
  two_fa_contact_name: z.string().optional(),
  two_fa_contact_email: z.string().optional(),
  two_fa_contact_phone: z.string().optional(),
});

export type EmailDomainFormValues = z.input<typeof emailDomainSchema>;

/** What a stage submission keeps: the domain plus only the selected option's fields. */
export interface EmailDomainSetup {
  setup_option: (typeof EMAIL_DOMAIN_SETUP_OPTIONS)[number];
  domain_source: (typeof EMAIL_DOMAIN_SOURCES)[number];
  domain: string;
  sendgrid_confirmed?: boolean;
  dns_records_domain?: string;
  access_method?: (typeof DOMAIN_ACCESS_METHODS)[number];
  godaddy_access_confirmed?: boolean;
  provider?: DomainProvider;
  provider_other_name?: string;
  login_url?: string;
  username?: string;
  password?: string;
  two_fa_contact_type?: TwoFaContactType;
  two_fa_contact_name?: string;
  two_fa_contact_email?: string;
  two_fa_contact_phone?: string;
}

/** The email domain for the chosen source: the IDX root domain, or the cleaned typed one. */
export const resolveEmailDomain = (
  source: EmailDomainSetup['domain_source'] | undefined,
  idxDisplayUrl: string,
  typedDomain: string | undefined
): string =>
  source === 'same_as_idx' ? toRootDomain(idxDisplayUrl) : cleanDomainInput(typedDomain ?? '');

/**
 * Whether the domain step is answered, so the next step (DNS records or
 * Speculo access) can show: the IDX domain with SendGrid confirmed, or a valid
 * typed domain.
 */
export const isEmailDomainReady = (
  source: EmailDomainSetup['domain_source'] | undefined,
  idxDisplayUrl: string,
  typedDomain: string | undefined,
  sendgridConfirmed: boolean | undefined
): boolean => {
  if (source === 'same_as_idx') return !!toRootDomain(idxDisplayUrl) && sendgridConfirmed === true;
  if (source === 'different') {
    return validateDomain(resolveEmailDomain(source, idxDisplayUrl, typedDomain)).isValid;
  }
  return false;
};

export const idxSetupSchema = z
  .object({
    idx_display_url: requiredWebUrl('IDX Display URL is required'),
    email_domain: emailDomainSchema,
  })
  .superRefine((data, ctx) => {
    const e = data.email_domain;
    const issue = (field: string, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ['email_domain', field] });

    if (!e.setup_option) issue('setup_option', 'Choose how you want to set up your email domain');
    if (!e.domain_source) issue('domain_source', 'Choose which domain Remi will send email from');

    if (e.domain_source === 'same_as_idx') {
      if (!toRootDomain(data.idx_display_url)) {
        issue('domain_source', 'Enter a valid IDX Display URL first');
      }
      if (e.sendgrid_confirmed !== true) {
        issue(
          'sendgrid_confirmed',
          'Please confirm this domain is not already connected to SendGrid'
        );
      }
    } else if (e.domain_source === 'different') {
      const domain = cleanDomainInput(e.domain ?? '');
      if (!domain) issue('domain', 'Domain is required');
      else if (!validateDomain(domain).isValid) {
        issue('domain', 'Please enter a valid domain (e.g., yourcompany.com)');
      }
    }

    if (e.setup_option === 'speculo') {
      if (!e.access_method) issue('access_method', 'Choose how to give Speculo access');
      if (e.access_method === 'godaddy_delegate' && e.godaddy_access_confirmed !== true) {
        issue('godaddy_access_confirmed', 'Please confirm you sent domain-only access to Speculo');
      }
      if (e.access_method === 'credentials') {
        if (!e.provider) issue('provider', 'Choose your domain provider');
        if (e.provider === 'other' && !e.provider_other_name?.trim()) {
          issue('provider_other_name', 'Enter your domain provider');
        }
        if (e.provider && e.provider !== 'godaddy' && !e.login_url?.trim()) {
          issue('login_url', 'Login URL is required');
        }
        if (!e.username?.trim()) issue('username', 'Username is required');
        if (!e.password?.trim()) issue('password', 'Password is required');
        addTwoFaContactIssues(e, ctx, ['email_domain']);
      }
    }
  })
  .transform((data) => {
    // Switching options keeps both in the form; only the selected one is submitted.
    const e = data.email_domain;
    const setup: EmailDomainSetup = {
      setup_option: e.setup_option!,
      domain_source: e.domain_source!,
      domain: resolveEmailDomain(e.domain_source, data.idx_display_url, e.domain),
      ...(e.domain_source === 'same_as_idx' && { sendgrid_confirmed: true }),
    };
    if (e.setup_option === 'self' && e.dns_records_domain === setup.domain) {
      setup.dns_records_domain = setup.domain;
    }
    if (e.setup_option === 'speculo') {
      setup.access_method = e.access_method;
      if (e.access_method === 'godaddy_delegate') {
        setup.godaddy_access_confirmed = true;
      } else {
        Object.assign(setup, {
          provider: e.provider,
          ...(e.provider === 'other' && { provider_other_name: e.provider_other_name?.trim() }),
          ...(e.provider !== 'godaddy' && { login_url: e.login_url }),
          username: e.username?.trim(),
          password: e.password,
          two_fa_contact_type: e.two_fa_contact_type,
          two_fa_contact_name: e.two_fa_contact_name?.trim(),
          two_fa_contact_email: e.two_fa_contact_email?.trim(),
          two_fa_contact_phone: e.two_fa_contact_phone,
        });
      }
    }
    return { idx_display_url: data.idx_display_url, email_domain: setup };
  });

export type IDXSetupFormValues = z.input<typeof idxSetupSchema>;
export type IDXSetupData = z.output<typeof idxSetupSchema>;

// Multiple CRMs setup schema
export const crmSetupSchema = z.object({
  crm_entries: z.array(crmEntrySchema).min(1, 'At least one CRM is required'),
});

export type CRMSetupData = z.infer<typeof crmSetupSchema>;

// Stage 4, "Create Remi's IDX for me": the original IDX setup, restored as it
// was before the IDX stage was simplified to the IDX Display URL (SPEC-2803).
// Stage 4: IDX Licensing Agreement & MLS Approval (Step 1 - MLS)
export const mlsEntrySchema = z.object({
  mls_name: z.string().min(1, 'MLS is required'),
  brokerage_name: z.string().min(1, 'Brokerage name is required'),
  broker_full_name: z.string().min(1, 'Broker full name is required'),
  broker_email: z.string().email('Valid broker email is required'),
  agent_name: z.string().min(1, 'Agent name is required'),
  agent_email: z.string().email('Valid agent email is required'),
  agent_mls_id: z.string().min(1, 'Agent MLS ID is required'),
});

export const mlsSetupSchema = z.object({
  mls_entries: z.array(mlsEntrySchema).min(1, 'At least one MLS entry is required'),
});

export type MLSSetupData = z.infer<typeof mlsSetupSchema>;

// Stage 4: IDX Site Setup (Step 2 - Site Configuration)
export const idxSiteSetupSchema = z
  .object({
    // Website type from Stage 2
    has_website: z.enum(['primary', 'secondary', 'no']),

    // Domain configuration
    idx_subdomain: z.string().optional(),
    new_domain: z.string().optional(),
    website_domain: z.string().optional(), // Pulled from Stage 2

    // Branding
    logo_url: z.string().min(1, 'Logo is required'),
    primary_color: z.string().optional(),
    secondary_color: z.string().optional(),

    // Compliance links
    privacy_policy_url: z.string().url().optional().or(z.literal('')),
    terms_of_service_url: z.string().url().optional().or(z.literal('')),
    disclaimer_url: z.string().url().optional().or(z.literal('')),

    // Remi's email domain (SPEC-2803): the IDX domain or another domain
    email_domain_source: z.enum(['same_as_idx', 'different']).optional(),
    email_domain: z.string().optional(),
    sendgrid_confirmed: z.boolean().optional(),

    // Domain access help (if needed)
    needs_domain_help: z.boolean().optional(),
    domain_access_shared: z.boolean().optional(), // Track if user already shared domain access
    domain_registrar: z.string().optional(),
    registrar_username: z.string().optional(),
    registrar_password: z.string().optional(),
    registrar_email: z.string().email().optional().or(z.literal('')),
    registrar_phone: z.string().optional(),
    // How Speculo gets into the domain provider: the same choices as Email Setup
    access_method: z.enum(DOMAIN_ACCESS_METHODS).optional(),
    provider: z.enum(DOMAIN_PROVIDER_VALUES).optional(),
    provider_other_name: z.string().optional(),
    login_url: optionalWebUrl,
    two_fa_contact_type: z.enum(TWO_FA_CONTACT_TYPES).optional(),
    two_fa_contact_name: z.string().optional(),
    two_fa_contact_email: z.string().optional(),
    two_fa_contact_phone: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    // Subdomain validation regex: lowercase letters, numbers, and hyphens only
    // Cannot start or end with hyphen, no consecutive hyphens
    const subdomainPattern = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

    // Validation based on website type
    if (data.has_website === 'primary' || data.has_website === 'secondary') {
      // Subdomain is required
      if (!data.idx_subdomain || data.idx_subdomain.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'IDX subdomain is required',
          path: ['idx_subdomain'],
        });
      } else if (!subdomainPattern.test(data.idx_subdomain.toLowerCase())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Subdomain must contain only lowercase letters, numbers, and hyphens (e.g., "homes" or "property-search")',
          path: ['idx_subdomain'],
        });
      }
    }

    if (data.has_website === 'no') {
      // New domain is required and must be valid format
      if (!data.new_domain || data.new_domain.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Domain is required',
          path: ['new_domain'],
        });
      } else {
        // Use shared domain validation (validates format, no http://, no paths, etc.)
        const validationResult = validateDomain(data.new_domain.trim());
        if (!validationResult.isValid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              validationResult.error || 'Please enter a valid domain name (e.g., yourcompany.com)',
            path: ['new_domain'],
          });
        }
      }
      // Subdomain for new domain is required
      if (!data.idx_subdomain || data.idx_subdomain.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'IDX subdomain is required',
          path: ['idx_subdomain'],
        });
      } else if (!subdomainPattern.test(data.idx_subdomain.toLowerCase())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Subdomain must contain only lowercase letters, numbers, and hyphens (e.g., "homes" or "property-search")',
          path: ['idx_subdomain'],
        });
      }
    }

    // "I'd like Speculo to set it up": the same access choices as Email Setup.
    if (data.needs_domain_help) {
      const issue = (field: string, message: string) =>
        ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [field] });
      if (!data.access_method) issue('access_method', 'Choose how to give Speculo access');
      if (data.access_method === 'godaddy_delegate' && data.domain_access_shared !== true) {
        issue('domain_access_shared', 'Please confirm you sent domain-only access to Speculo');
      }
      if (data.access_method === 'credentials') {
        if (!data.provider) issue('provider', 'Choose your domain provider');
        if (data.provider === 'other' && !data.provider_other_name?.trim()) {
          issue('provider_other_name', 'Enter your domain provider');
        }
        if (data.provider && data.provider !== 'godaddy' && !data.login_url?.trim()) {
          issue('login_url', 'Login URL is required');
        }
        if (!data.registrar_username?.trim()) issue('registrar_username', 'Username is required');
        if (!data.registrar_password?.trim()) issue('registrar_password', 'Password is required');
        addTwoFaContactIssues(data, ctx);
      }
    }
  });

export type IDXSiteSetupData = z.infer<typeof idxSiteSetupSchema>;

// Combined Remi's IDX data (both steps)
export const remiIdxSetupSchema = z.object({
  mls_data: mlsSetupSchema,
  site_data: idxSiteSetupSchema.optional(),
});

export type RemiIdxSetupData = z.infer<typeof remiIdxSetupSchema>;
